'use client'

import { Select as BaseSelect, type SelectRootActions } from '@base-ui/react/select'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useImperativeHandle, useRef, useState, type AriaAttributes, type Ref } from 'react'

import { breakpoints, colors, controlSizes, fonts, fontSizes, layers, radii, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  compact: {
    minHeight: { default: controlSizes.compact, [breakpoints.coarse]: controlSizes.touch },
    paddingInline: spacing.space8,
    fontSize: fontSizes.small,
    borderColor: colors.border,
  },
  compactPopup: { fontSize: fontSizes.small },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.space12,
    minHeight: 40,
    paddingInline: spacing.space12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.borderStrong,
    borderRadius: radii.small,
    backgroundColor: { default: colors.background, ':disabled': colors.surfaceAlt },
    color: { default: colors.foreground, ':disabled': colors.muted },
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    cursor: { default: 'pointer', ':disabled': 'not-allowed' },
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: 3,
  },
  positioner: { zIndex: layers.popup },
  popup: {
    minWidth: 'var(--anchor-width)',
    maxHeight: 'var(--available-height)',
    overflowY: 'auto',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.borderStrong,
    borderRadius: radii.small,
    backgroundColor: colors.background,
    padding: spacing.space4,
    color: colors.foreground,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.space16,
    paddingBlock: spacing.space8,
    paddingInline: spacing.space12,
    borderRadius: radii.small,
    cursor: 'pointer',
    outline: 'none',
    backgroundColor: { default: 'transparent', ':is([data-highlighted])': colors.surfaceAlt },
    color: { default: colors.foreground, ':is([data-disabled])': colors.muted },
  },
})

export type SelectOption<Value extends string = string> = { value: Value; label: string; disabled?: boolean }

type SelectOptions<Value extends string> = {
  'aria-label': string
  disabled?: boolean | undefined
  form?: string
  id?: string
  name?: string
  onValueChange(value: Value): void
  options: readonly SelectOption<Value>[]
  readOnly?: boolean
  ref?: Ref<HTMLButtonElement>
  required?: boolean
  size?: 'md' | 'sm'
  value: Value
}

export type SelectProps<Value extends string> = Pick<AriaAttributes, 'aria-describedby' | 'aria-invalid'> &
  SelectOptions<Value>

/**
 * Choose one value from a known set; use Combobox for searched options or SegmentedControl for a few visible
 * choices. Applications supply ordered options and own any acted-on selection. Selection changes on
 * activation, not hover.
 *
 * Status: supported.
 */
export function Select<Value extends string>({
  'aria-describedby': description,
  'aria-invalid': invalid,
  'aria-label': label,
  disabled,
  form,
  id,
  name,
  onValueChange,
  options,
  readOnly,
  ref,
  required,
  size = 'md',
  value,
}: SelectProps<Value>) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const actionsRef = useRef<SelectRootActions>(null)
  const [open, setOpen] = useState(false)
  const [portalContainer, setPortalContainer] = useState<HTMLDialogElement>()

  useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(ref, () => triggerRef.current, [])

  useEffect(() => {
    const trigger = triggerRef.current

    if (!open || trigger === null) return

    // A native fieldset can disable the trigger without notifying the portaled popup.
    const observer = new MutationObserver(() => {
      if (trigger.matches(':disabled')) {
        actionsRef.current?.close()
      } else {
        observeAncestors()
      }
    })

    function observeAncestors() {
      observer.disconnect()

      for (let node: HTMLElement | null = trigger; node; node = node.parentElement) {
        observer.observe(node, { attributes: true, attributeFilter: ['disabled'], childList: true })
      }
    }

    observeAncestors()

    if (trigger.matches(':disabled')) actionsRef.current?.close()

    return () => observer.disconnect()
  }, [open])

  return (
    <BaseSelect.Root
      actionsRef={actionsRef}
      disabled={disabled}
      form={form}
      id={id}
      items={options}
      name={name}
      onOpenChange={(next) => {
        // Body portals sit behind a native modal dialog's top layer.
        setPortalContainer(triggerRef.current?.closest('dialog') ?? undefined)
        setOpen(next)
      }}
      onValueChange={(next, details) => {
        if (triggerRef.current?.matches(':disabled')) {
          details.cancel()

          return
        }

        if (next !== null) onValueChange(next)
      }}
      readOnly={readOnly}
      required={required}
      value={value}
    >
      <BaseSelect.Trigger
        ref={triggerRef}
        aria-describedby={description}
        aria-invalid={invalid}
        aria-label={label}
        {...stylex.props(styles.trigger, size === 'sm' && styles.compact)}
      >
        <BaseSelect.Value />
        <BaseSelect.Icon aria-hidden>⌄</BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal container={portalContainer}>
        <BaseSelect.Positioner sideOffset={4} alignItemWithTrigger={false} {...stylex.props(styles.positioner)}>
          <BaseSelect.Popup {...stylex.props(styles.popup, size === 'sm' && styles.compactPopup)}>
            <BaseSelect.List aria-label={label}>
              {options.map((option) => (
                <BaseSelect.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  {...stylex.props(styles.item)}
                >
                  <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                  <BaseSelect.ItemIndicator aria-hidden>✓</BaseSelect.ItemIndicator>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}
